import React, { useEffect, useMemo, useState, useRef } from 'react'

import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Chip,
    Checkbox,
    Grid,
    Divider,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
} from '@material-ui/core'
import {
    ArrowBack as ArrowLeft,
    Add,
    Delete,
    Mail as MailIcon,
    Message as MessageSquareIcon,
    Schedule as DelayIcon,
    ExpandMore,
    ExpandLess,
    AutorenewRounded as AIIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    SequenceStep,
    DelayStep,
    SequenceItem,
    WeeklySchedule,
    DaySchedule,
    TIME_OPTIONS,
} from './types'
import { useGuestCards } from '../../../../hooks/Outreach/useGuestCards'
import { useGuestCardChannelDefaults } from '../../../../hooks/Outreach/useGuestCardChannelDefaults'
import { CreateGuestCardModal } from '../GuestCards/CreateGuestCardModal'
import {
    useCampaign,
    AIGenerationRequest,
} from '../../../../hooks/Outreach/useCampaign'
import { RichTextEmailEditor } from './RichTextEmailEditor'
import { MergeVariableDropdown } from './MergeVariableDropdown'
import { FileAttachments } from './FileAttachments'

const useStyles = makeStyles((theme: Theme) => ({
    headerCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
    },
    stepCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(2),
        borderRadius: '12px',
    },
    delayCard: {
        border: 'none',
        backgroundColor: '#fef3c7',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(2),
        borderRadius: '12px',
        padding: theme.spacing(2),
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2),
        borderBottom: '1px solid #e2e8f0',
    },
    stepIconContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    stepIcon: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    stepContent: {
        padding: theme.spacing(3),
    },
    stepSidebar: {
        padding: theme.spacing(3),
        borderLeft: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
    },
    formField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover fieldset': {
                borderColor: '#3b82f6',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
                borderWidth: 2,
            },
        },
        '& .MuiInputLabel-root': {
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: 500,
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#3b82f6',
        },
    },
    dayRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        borderBottom: '1px solid #e2e8f0',
        gap: theme.spacing(1),
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    dayLabel: {
        minWidth: 60,
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    timeInput: {
        width: 120,
        [theme.breakpoints.down('sm')]: {
            width: 100,
            minWidth: 90,
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1),
            fontSize: '0.875rem',
        },
    },
    connector: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: theme.spacing(1, 0),
    },
    connectorDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#10b981',
        marginBottom: theme.spacing(0.5),
    },
    connectorLine: {
        width: 2,
        height: 20,
        backgroundColor: '#10b981',
    },
    addDelayButton: {
        margin: theme.spacing(1, 0),
        width: '100%',
        borderStyle: 'dashed',
        borderColor: '#d1d5db',
        borderWidth: 2,
        borderRadius: '8px',
        padding: theme.spacing(1.5),
        backgroundColor: 'transparent',
        color: '#6b7280',
        '&:hover': {
            backgroundColor: '#f8fafc',
            borderColor: '#3b82f6',
            color: '#3b82f6',
        },
    },
    navigationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
    },
    gradientButton: {
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        borderRadius: '8px',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(8),
    },
    emptyStateIcon: {
        padding: theme.spacing(2),
        borderRadius: '50%',
        backgroundColor: '#f1f5f9',
        marginBottom: theme.spacing(2),
        display: 'inline-flex',
    },
    deleteDialogIcon: {
        padding: theme.spacing(2),
        borderRadius: '50%',
        backgroundColor: '#fee2e2',
        color: '#ef4444',
        marginBottom: theme.spacing(2),
        display: 'inline-flex',
        margin: '0 auto',
    },
    deleteDialogTitle: {
        textAlign: 'center',
        paddingTop: theme.spacing(2),
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    deleteDialogContent: {
        textAlign: 'center',
        paddingBottom: theme.spacing(2),
        color: '#6b7280',
    },
    deleteDialogActions: {
        padding: theme.spacing(2, 3),
        gap: theme.spacing(1),
    },
    deleteButton: {
        backgroundColor: '#ef4444',
        color: 'white',
        '&:hover': {
            backgroundColor: '#dc2626',
        },
    },
    aiButton: {
        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
        color: 'white',
        borderRadius: '8px',
        padding: '10px 20px',
        '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
            boxShadow:
                '0 10px 15px -3px rgba(139, 92, 246, 0.3), 0 4px 6px -2px rgba(139, 92, 246, 0.2)',
        },
        '&:disabled': {
            background: '#e2e8f0',
            color: '#94a3b8',
        },
    },
    aiDialog: {
        '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '16px',
            minWidth: '650px',
            maxWidth: '750px',
        },
    },
    aiDialogTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1f2937',
    },
    aiDialogIcon: {
        padding: '12px',
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    aiDialogContent: {
        padding: '16px 24px',
    },
    aiGenerateButton: {
        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        },
        '&:disabled': {
            background: '#e2e8f0',
            color: '#94a3b8',
        },
    },
}))

const defaultSchedule: WeeklySchedule = {
    sun: { enabled: false, startTime: '12:00 AM', endTime: '12:00 AM' },
    mon: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    tue: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    wed: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    thu: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    fri: { enabled: true, startTime: '08:00 AM', endTime: '06:00 PM' },
    sat: { enabled: false, startTime: '12:00 AM', endTime: '12:00 AM' },
}

interface CampaignSequenceTabProps {
    sequence: SequenceItem[]
    setSequence: React.Dispatch<React.SetStateAction<SequenceItem[]>>
    channels: { email: boolean; sms: boolean }
    campaignName?: string
    campaignGoal?: string
    onBack: () => void
    onNext: () => void
    backLabel?: string
    nextLabel?: string
}

const isDelayStep = (item: SequenceItem): item is DelayStep => {
    return item.type === 'delay'
}

const isMessageStep = (item: SequenceItem): item is SequenceStep => {
    return item.type === 'email' || item.type === 'sms'
}

export const CampaignSequenceTab: React.FC<CampaignSequenceTabProps> = ({
    sequence,
    setSequence,
    channels,
    campaignName,
    campaignGoal,
    onBack,
    onNext,
    backLabel = 'Back to Setup',
    nextLabel = 'Continue to Launch →',
}) => {
    const classes = useStyles()
    const { guestCards } = useGuestCards()
    const { defaults: guestCardDefaults } = useGuestCardChannelDefaults()
    const defaultCampaignGuestCardId = guestCardDefaults.campaign
    const sortedGuestCards = useMemo(() => {
        const cards = guestCards ?? []
        if (defaultCampaignGuestCardId == null) return cards
        return [...cards].sort((a, b) => {
            if (a.id === defaultCampaignGuestCardId) return -1
            if (b.id === defaultCampaignGuestCardId) return 1
            return 0
        })
    }, [guestCards, defaultCampaignGuestCardId])
    const { generateAIEmails } = useCampaign()
    const [collapsedSteps, setCollapsedSteps] = useState<Set<string>>(new Set())
    const [editingStep, setEditingStep] = useState<SequenceStep | null>(null)

    // When the season campaign default loads, fill any message steps that
    // still have no guest card attached.
    useEffect(() => {
        if (defaultCampaignGuestCardId == null) return
        setSequence((prev) => {
            let changed = false
            const next = prev.map((item) => {
                if (!isMessageStep(item) || item.guest_card) return item
                changed = true
                return { ...item, guest_card: defaultCampaignGuestCardId }
            })
            return changed ? next : prev
        })
    }, [defaultCampaignGuestCardId, setSequence])
    const [addStepMenuAnchor, setAddStepMenuAnchor] = useState<{
        element: HTMLElement
        index: number
    } | null>(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [stepToDelete, setStepToDelete] = useState<string | null>(null)
    const [createGuestCardModalOpen, setCreateGuestCardModalOpen] = useState(
        false,
    )
    const [currentStepForGuestCard, setCurrentStepForGuestCard] = useState<
        string | null
    >(null)
    const scrollPositionRef = useRef<number>(0)

    // AI Generation state
    const [aiDialogOpen, setAiDialogOpen] = useState(false)
    const [aiNumSteps, setAiNumSteps] = useState(3)
    const [aiTone, setAiTone] = useState('professional')
    const [aiAdditionalContext, setAiAdditionalContext] = useState('')

    // Refs for inserting merge variables into rich text editors
    const editorInsertRefs = useRef<Record<string, (text: string) => void>>({})

    const handleGenerateAI = () => {
        const channelType =
            channels.email && channels.sms
                ? 'both'
                : channels.email
                ? 'email'
                : 'sms'

        const request: AIGenerationRequest = {
            campaign_name: campaignName,
            campaign_goal: campaignGoal,
            num_steps: aiNumSteps,
            channel_type: channelType,
            tone: aiTone,
            prompt: aiAdditionalContext || undefined,
        }

        generateAIEmails.mutate(request, {
            onSuccess: (response) => {
                // Handle different possible response structures
                let responseData = response.data

                // Handle string response (parse JSON if needed)
                if (typeof responseData === 'string') {
                    try {
                        responseData = JSON.parse(responseData)
                    } catch (e) {
                        console.error('Failed to parse AI response:', e)
                        return
                    }
                }

                let generatedEmails: Array<{
                    subject?: string
                    content?: string
                }> = []

                if (Array.isArray(responseData)) {
                    // Response is directly an array
                    generatedEmails = responseData
                } else if (
                    responseData?.emails &&
                    Array.isArray(responseData.emails)
                ) {
                    // Response has emails array
                    generatedEmails = responseData.emails
                } else if (
                    responseData?.steps &&
                    Array.isArray(responseData.steps)
                ) {
                    // Response has steps array
                    generatedEmails = responseData.steps
                } else if (
                    responseData?.data &&
                    Array.isArray(responseData.data)
                ) {
                    // Response has data array
                    generatedEmails = responseData.data
                }

                if (generatedEmails.length === 0) {
                    console.log(
                        'No emails generated from response:',
                        responseData,
                    )
                    return
                }

                // Clear any stale state from previous sequence first
                setCollapsedSteps(new Set())
                setEditingStep(null)

                // Create new sequence items from AI-generated content
                const newSequenceItems: SequenceItem[] = []
                const timestamp = Date.now()

                generatedEmails.forEach((email, index) => {
                    // Add delay between steps (except before the first step)
                    if (index > 0) {
                        const delayStep: DelayStep = {
                            id: `delay-ai-${timestamp}-${index}`,
                            type: 'delay',
                            days: 3,
                        }
                        newSequenceItems.push(delayStep)
                    }

                    // Determine step type based on channel
                    const stepType = channels.email ? 'email' : 'sms'

                    const messageStep: SequenceStep = {
                        id: `step-ai-${timestamp}-${index}`,
                        type: stepType,
                        subject: email.subject || '',
                        message: email.content || '',
                        content_html: (email as any).content_html || undefined,
                        schedule: defaultSchedule,
                        guest_card: defaultCampaignGuestCardId,
                    }
                    newSequenceItems.push(messageStep)
                })

                // Replace entire sequence with AI-generated content
                setSequence(newSequenceItems)

                setAiDialogOpen(false)
                setAiAdditionalContext('')
            },
        })
    }

    const handleAddStep = (type: 'email' | 'sms') => {
        const newStep: SequenceStep = {
            id: `step-${Date.now()}`,
            type,
            subject: '',
            message: '',
            schedule: defaultSchedule,
            guest_card: defaultCampaignGuestCardId,
        }
        setSequence([...sequence, newStep])
        // New steps are expanded by default (not in collapsed set)
        setEditingStep(newStep)
    }

    const handleAddStepAfter = (afterIndex: number, type: 'email' | 'sms') => {
        // Save current scroll position
        scrollPositionRef.current =
            window.scrollY || document.documentElement.scrollTop

        const newStep: SequenceStep = {
            id: `step-${Date.now()}`,
            type,
            subject: '',
            message: '',
            schedule: defaultSchedule,
            guest_card: defaultCampaignGuestCardId,
        }
        const newSequence = [...sequence]

        // If afterIndex is -1, append to the end
        if (afterIndex === -1) {
            // If sequence is empty, just add the step
            if (sequence.length === 0) {
                newSequence.push(newStep)
            } else {
                // Automatically add a delay step before the new step when appending
                const delayStep: DelayStep = {
                    id: `delay-${Date.now()}`,
                    type: 'delay',
                    days: 3,
                }
                newSequence.push(delayStep, newStep)
            }
        } else if (afterIndex >= 0) {
            // Automatically add a delay step before the new step
            const delayStep: DelayStep = {
                id: `delay-${Date.now()}`,
                type: 'delay',
                days: 3,
            }
            newSequence.splice(afterIndex + 1, 0, delayStep, newStep)
        }

        setSequence(newSequence)
        // New steps are expanded by default (not in collapsed set)
        setEditingStep(newStep)
        setAddStepMenuAnchor(null)

        // Restore scroll position after React updates
        setTimeout(() => {
            window.scrollTo(0, scrollPositionRef.current)
        }, 0)
    }

    const handleAddDelay = (afterIndex: number) => {
        const newDelay: DelayStep = {
            id: `delay-${Date.now()}`,
            type: 'delay',
            days: 3,
        }
        const newSequence = [...sequence]
        newSequence.splice(afterIndex + 1, 0, newDelay)
        setSequence(newSequence)
    }

    const handleDeleteClick = (stepId: string) => {
        setStepToDelete(stepId)
        setDeleteConfirmOpen(true)
    }

    const handleDeleteConfirm = () => {
        if (stepToDelete) {
            setSequence(sequence.filter((s) => s.id !== stepToDelete))
            setCollapsedSteps((prev) => {
                const newSet = new Set(prev)
                newSet.delete(stepToDelete)
                return newSet
            })
            if (editingStep?.id === stepToDelete) {
                setEditingStep(null)
            }
        }
        setDeleteConfirmOpen(false)
        setStepToDelete(null)
    }

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false)
        setStepToDelete(null)
    }

    const handleUpdateStep = (updatedStep: SequenceStep) => {
        setSequence(
            sequence.map((s) => (s.id === updatedStep.id ? updatedStep : s)),
        )
        setEditingStep(null)
    }

    const handleToggleExpand = (stepId: string) => {
        const step = sequence.find((s) => s.id === stepId)
        if (step && isMessageStep(step)) {
            setCollapsedSteps((prev) => {
                const newSet = new Set(prev)
                if (newSet.has(stepId)) {
                    // Expand: remove from collapsed set
                    newSet.delete(stepId)
                    // Initialize editing step if not already set
                    if (!editingStep || editingStep.id !== stepId) {
                        setEditingStep({
                            ...step,
                            schedule: step.schedule || defaultSchedule,
                        })
                    }
                } else {
                    // Collapse: add to collapsed set
                    newSet.add(stepId)
                    if (editingStep?.id === stepId) {
                        setEditingStep(null)
                    }
                }
                return newSet
            })
        }
    }

    const handleScheduleChange = (
        stepId: string,
        day: keyof WeeklySchedule,
        field: keyof DaySchedule,
        value: boolean | string,
    ) => {
        const step = sequence.find((s) => s.id === stepId)
        if (step && isMessageStep(step)) {
            const updatedSchedule = {
                ...(step.schedule || defaultSchedule),
                [day]: {
                    ...(step.schedule || defaultSchedule)[day],
                    [field]: value,
                },
            }
            handleUpdateStep({ ...step, schedule: updatedSchedule })
        }
    }

    const getStepIcon = (type: SequenceStep['type']) => {
        switch (type) {
            case 'email':
                return MailIcon
            case 'sms':
                return MessageSquareIcon
            default:
                return MailIcon
        }
    }

    const getStepColor = (type: SequenceStep['type']) => {
        switch (type) {
            case 'email':
                return '#3b82f6'
            case 'sms':
                return '#10b981'
            default:
                return '#3b82f6'
        }
    }

    const messageSteps = sequence.filter(isMessageStep)

    // Validate that all message steps have a guest card
    const stepsWithoutGuestCard = messageSteps.filter(
        (step) => !step.guest_card,
    )
    const isValid =
        messageSteps.length > 0 && stepsWithoutGuestCard.length === 0

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 24,
                width: '100%',
            }}
        >
            {/* Header */}
            <Card className={classes.headerCard}>
                <CardContent>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Message Sequence Builder
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Build your multi-step campaign sequence.
                                Configure each step with content and scheduling.
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AIIcon />}
                            onClick={() => setAiDialogOpen(true)}
                            className={classes.aiButton}
                        >
                            Generate with AI
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Sequence Steps */}
            {sequence.length === 0 ? (
                <Card className={classes.stepCard}>
                    <CardContent className={classes.emptyState}>
                        <Box className={classes.emptyStateIcon}>
                            <MailIcon
                                style={{ fontSize: 32, color: '#94a3b8' }}
                            />
                        </Box>
                        <Typography variant="h6" gutterBottom>
                            No sequence steps yet
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            paragraph
                        >
                            Start building your campaign sequence by adding your
                            first step.
                        </Typography>
                        <Box
                            display="flex"
                            justifyContent="center"
                            style={{ gap: 8 }}
                        >
                            {channels.email && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<MailIcon />}
                                    onClick={() => handleAddStep('email')}
                                >
                                    Add Email Step
                                </Button>
                            )}
                            {channels.sms && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<MessageSquareIcon />}
                                    onClick={() => handleAddStep('sms')}
                                >
                                    Add SMS Step
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Box>
                    {sequence.map((item, index) => {
                        if (isDelayStep(item)) {
                            return (
                                <Box key={item.id}>
                                    <Box className={classes.connector}>
                                        <Box className={classes.connectorDot} />
                                        <Box
                                            className={classes.connectorLine}
                                        />
                                    </Box>
                                    <Card className={classes.delayCard}>
                                        <CardContent>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 12 }}
                                                >
                                                    <DelayIcon
                                                        style={{
                                                            color: '#f59e0b',
                                                            fontSize: 24,
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            variant="body1"
                                                            style={{
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            Wait {item.days}{' '}
                                                            {item.days === 1
                                                                ? 'day'
                                                                : 'days'}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Delay before next
                                                            step
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 8 }}
                                                >
                                                    <TextField
                                                        type="number"
                                                        value={item.days}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement>,
                                                        ) => {
                                                            const newDelay: DelayStep = {
                                                                ...item,
                                                                days:
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ) || 0,
                                                            }
                                                            setSequence(
                                                                sequence.map(
                                                                    (s) =>
                                                                        s.id ===
                                                                        item.id
                                                                            ? newDelay
                                                                            : s,
                                                                ),
                                                            )
                                                        }}
                                                        inputProps={{
                                                            min: 0,
                                                            style: {
                                                                padding: 8,
                                                                width: 60,
                                                            },
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleDeleteClick(
                                                                item.id,
                                                            )
                                                        }
                                                        style={{
                                                            color: '#ef4444',
                                                        }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )
                        }

                        const step = item as SequenceStep
                        const StepIcon = getStepIcon(step.type)
                        const stepColor = getStepColor(step.type)
                        const isExpanded = !collapsedSteps.has(step.id)
                        const isFirstStep = index === 0

                        return (
                            <Box key={step.id}>
                                {index > 0 && (
                                    <Box className={classes.connector}>
                                        <Box className={classes.connectorDot} />
                                        <Box
                                            className={classes.connectorLine}
                                        />
                                    </Box>
                                )}

                                <Card className={classes.stepCard}>
                                    {/* Step Header */}
                                    <Box className={classes.stepHeader}>
                                        <Box
                                            className={
                                                classes.stepIconContainer
                                            }
                                        >
                                            <Box
                                                className={classes.stepIcon}
                                                style={{
                                                    backgroundColor: `${stepColor}15`,
                                                }}
                                            >
                                                <StepIcon
                                                    style={{
                                                        color: stepColor,
                                                        fontSize: 20,
                                                    }}
                                                />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    variant="body1"
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    {step.type.toUpperCase()}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                >
                                                    Step{' '}
                                                    {messageSteps.findIndex(
                                                        (s) => s.id === step.id,
                                                    ) + 1}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8 }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleToggleExpand(step.id)
                                                }
                                                style={{ color: '#6b7280' }}
                                            >
                                                {isExpanded ? (
                                                    <ExpandLess />
                                                ) : (
                                                    <ExpandMore />
                                                )}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeleteClick(step.id)
                                                }
                                                style={{ color: '#ef4444' }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {/* Expanded Content */}
                                    {isExpanded && (
                                        <Grid container>
                                            <Grid item xs={12} md={7}>
                                                <Box
                                                    className={
                                                        classes.stepContent
                                                    }
                                                >
                                                    {/* Step Type Selector */}
                                                    <FormControl
                                                        fullWidth
                                                        className={
                                                            classes.formField
                                                        }
                                                        variant="outlined"
                                                    >
                                                        <InputLabel>
                                                            Change step
                                                        </InputLabel>
                                                        <Select
                                                            value={step.type}
                                                            onChange={(
                                                                e: React.ChangeEvent<{
                                                                    value: unknown
                                                                }>,
                                                            ) => {
                                                                handleUpdateStep(
                                                                    {
                                                                        ...step,
                                                                        type: e
                                                                            .target
                                                                            .value as
                                                                            | 'email'
                                                                            | 'sms',
                                                                    },
                                                                )
                                                            }}
                                                            label="Change step"
                                                        >
                                                            {channels.email && (
                                                                <MenuItem value="email">
                                                                    Email
                                                                </MenuItem>
                                                            )}
                                                            {channels.sms && (
                                                                <MenuItem value="sms">
                                                                    SMS
                                                                </MenuItem>
                                                            )}
                                                        </Select>
                                                    </FormControl>

                                                    {/* Subject (only for email) */}
                                                    {step.type === 'email' && (
                                                        <Box
                                                            style={{
                                                                position:
                                                                    'relative',
                                                            }}
                                                        >
                                                            <TextField
                                                                fullWidth
                                                                label="Subject"
                                                                placeholder="Leave the subject blank to follow up in the same thread"
                                                                value={
                                                                    step.subject ||
                                                                    ''
                                                                }
                                                                onChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                                ) => {
                                                                    const updatedStep = {
                                                                        ...step,
                                                                        subject:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    }
                                                                    setSequence(
                                                                        sequence.map(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id ===
                                                                                step.id
                                                                                    ? updatedStep
                                                                                    : s,
                                                                        ),
                                                                    )
                                                                }}
                                                                className={
                                                                    classes.formField
                                                                }
                                                                variant="outlined"
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <MergeVariableDropdown
                                                                            onSelect={(
                                                                                variable,
                                                                            ) => {
                                                                                const updatedStep = {
                                                                                    ...step,
                                                                                    subject:
                                                                                        (step.subject ||
                                                                                            '') +
                                                                                        variable,
                                                                                }
                                                                                setSequence(
                                                                                    sequence.map(
                                                                                        (
                                                                                            s,
                                                                                        ) =>
                                                                                            s.id ===
                                                                                            step.id
                                                                                                ? updatedStep
                                                                                                : s,
                                                                                    ),
                                                                                )
                                                                            }}
                                                                        />
                                                                    ),
                                                                }}
                                                            />
                                                        </Box>
                                                    )}

                                                    {/* Message Content */}
                                                    {step.type === 'email' ? (
                                                        <Box
                                                            className={
                                                                classes.formField
                                                            }
                                                        >
                                                            <RichTextEmailEditor
                                                                value={
                                                                    step.message
                                                                }
                                                                htmlValue={
                                                                    step.content_html
                                                                }
                                                                onChange={(
                                                                    plainText,
                                                                    html,
                                                                ) => {
                                                                    const updatedStep = {
                                                                        ...step,
                                                                        message: plainText,
                                                                        content_html: html,
                                                                    }
                                                                    setSequence(
                                                                        sequence.map(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id ===
                                                                                step.id
                                                                                    ? updatedStep
                                                                                    : s,
                                                                        ),
                                                                    )
                                                                }}
                                                                onInsertRef={(
                                                                    insertFn,
                                                                ) => {
                                                                    editorInsertRefs.current[
                                                                        step.id
                                                                    ] = insertFn
                                                                }}
                                                                toolbarExtra={
                                                                    <MergeVariableDropdown
                                                                        onSelect={(
                                                                            variable,
                                                                        ) => {
                                                                            const insertFn =
                                                                                editorInsertRefs
                                                                                    .current[
                                                                                    step
                                                                                        .id
                                                                                ]
                                                                            if (
                                                                                insertFn
                                                                            ) {
                                                                                insertFn(
                                                                                    variable,
                                                                                )
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <TextField
                                                            fullWidth
                                                            label="Message"
                                                            multiline
                                                            rows={8}
                                                            placeholder="Type in your follow-up or generate it with AI"
                                                            value={(
                                                                step.message ||
                                                                ''
                                                            ).replace(
                                                                /\\n/g,
                                                                '\n',
                                                            )}
                                                            onChange={(
                                                                e: React.ChangeEvent<HTMLInputElement>,
                                                            ) => {
                                                                const updatedStep = {
                                                                    ...step,
                                                                    message:
                                                                        e.target
                                                                            .value,
                                                                }
                                                                setSequence(
                                                                    sequence.map(
                                                                        (s) =>
                                                                            s.id ===
                                                                            step.id
                                                                                ? updatedStep
                                                                                : s,
                                                                    ),
                                                                )
                                                            }}
                                                            className={
                                                                classes.formField
                                                            }
                                                            variant="outlined"
                                                        />
                                                    )}

                                                    {/* Attachments (email only) */}
                                                    {step.type === 'email' && (
                                                        <Box
                                                            className={
                                                                classes.formField
                                                            }
                                                        >
                                                            <FileAttachments
                                                                mode="local"
                                                                pendingFiles={
                                                                    step.pendingFiles ||
                                                                    []
                                                                }
                                                                onPendingFilesChange={(
                                                                    files,
                                                                ) => {
                                                                    const updatedStep = {
                                                                        ...step,
                                                                        pendingFiles: files,
                                                                    }
                                                                    setSequence(
                                                                        sequence.map(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id ===
                                                                                step.id
                                                                                    ? updatedStep
                                                                                    : s,
                                                                        ),
                                                                    )
                                                                }}
                                                            />
                                                        </Box>
                                                    )}

                                                    {/* Guest Card Selector */}
                                                    <Box
                                                        display="flex"
                                                        alignItems="flex-start"
                                                        style={{ gap: 8 }}
                                                    >
                                                        <FormControl
                                                            fullWidth
                                                            className={
                                                                classes.formField
                                                            }
                                                            variant="outlined"
                                                            style={{
                                                                marginBottom: 0,
                                                            }}
                                                            required
                                                            error={
                                                                !step.guest_card
                                                            }
                                                        >
                                                            <InputLabel>
                                                                Attach Guest
                                                                Card (Required)
                                                            </InputLabel>
                                                            <Select
                                                                value={
                                                                    step.guest_card ||
                                                                    ''
                                                                }
                                                                onChange={(
                                                                    e: React.ChangeEvent<{
                                                                        value: unknown
                                                                    }>,
                                                                ) => {
                                                                    const value =
                                                                        e.target
                                                                            .value
                                                                    if (
                                                                        value ===
                                                                        'CREATE_NEW'
                                                                    ) {
                                                                        setCurrentStepForGuestCard(
                                                                            step.id,
                                                                        )
                                                                        setCreateGuestCardModalOpen(
                                                                            true,
                                                                        )
                                                                        // Don't update the step value, so the Select reverts to previous value
                                                                        return
                                                                    }
                                                                    const updatedStep = {
                                                                        ...step,
                                                                        guest_card: value
                                                                            ? (value as number)
                                                                            : null,
                                                                    }
                                                                    setSequence(
                                                                        sequence.map(
                                                                            (
                                                                                s,
                                                                            ) =>
                                                                                s.id ===
                                                                                step.id
                                                                                    ? updatedStep
                                                                                    : s,
                                                                        ),
                                                                    )
                                                                }}
                                                                label="Attach Guest Card (Required)"
                                                            >
                                                                {sortedGuestCards.map(
                                                                    (card) => (
                                                                        <MenuItem
                                                                            key={
                                                                                card.id
                                                                            }
                                                                            value={
                                                                                card.id
                                                                            }
                                                                        >
                                                                            {
                                                                                card.card_name
                                                                            }
                                                                            {card.id ===
                                                                            defaultCampaignGuestCardId
                                                                                ? ' (season default)'
                                                                                : ''}
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                                <Divider />
                                                                <MenuItem
                                                                    value="CREATE_NEW"
                                                                    style={{
                                                                        color:
                                                                            '#3b82f6',
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    <Add
                                                                        style={{
                                                                            marginRight: 8,
                                                                            fontSize: 18,
                                                                        }}
                                                                    />
                                                                    Create New
                                                                    Guest Card
                                                                </MenuItem>
                                                            </Select>
                                                            {!step.guest_card && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="error"
                                                                    style={{
                                                                        marginTop: 4,
                                                                        marginLeft: 14,
                                                                    }}
                                                                >
                                                                    A guest card
                                                                    is required
                                                                    for this
                                                                    step
                                                                </Typography>
                                                            )}
                                                        </FormControl>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} md={5}>
                                                <Box
                                                    className={
                                                        classes.stepSidebar
                                                    }
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        gutterBottom
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        Schedule
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        paragraph
                                                    >
                                                        Select days and times
                                                        when this step should
                                                        execute
                                                    </Typography>

                                                    <Divider
                                                        style={{
                                                            marginBottom: 16,
                                                        }}
                                                    />

                                                    {([
                                                        'sun',
                                                        'mon',
                                                        'tue',
                                                        'wed',
                                                        'thu',
                                                        'fri',
                                                        'sat',
                                                    ] as const).map((day) => {
                                                        const daySchedule = (step.schedule ||
                                                            defaultSchedule)[
                                                            day
                                                        ]
                                                        const dayLabels = {
                                                            sun: 'SUN',
                                                            mon: 'MON',
                                                            tue: 'TUE',
                                                            wed: 'WED',
                                                            thu: 'THU',
                                                            fri: 'FRI',
                                                            sat: 'SAT',
                                                        }

                                                        return (
                                                            <Box
                                                                key={day}
                                                                className={
                                                                    classes.dayRow
                                                                }
                                                            >
                                                                <Checkbox
                                                                    checked={
                                                                        daySchedule.enabled
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        handleScheduleChange(
                                                                            step.id,
                                                                            day,
                                                                            'enabled',
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    style={{
                                                                        padding: 4,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    className={
                                                                        classes.dayLabel
                                                                    }
                                                                >
                                                                    {
                                                                        dayLabels[
                                                                            day
                                                                        ]
                                                                    }
                                                                </Typography>
                                                                {daySchedule.enabled ? (
                                                                    <>
                                                                        <FormControl
                                                                            size="small"
                                                                            variant="outlined"
                                                                            className={
                                                                                classes.timeInput
                                                                            }
                                                                        >
                                                                            <Select
                                                                                value={
                                                                                    daySchedule.startTime
                                                                                }
                                                                                onChange={(
                                                                                    e: React.ChangeEvent<{
                                                                                        value: unknown
                                                                                    }>,
                                                                                ) =>
                                                                                    handleScheduleChange(
                                                                                        step.id,
                                                                                        day,
                                                                                        'startTime',
                                                                                        e
                                                                                            .target
                                                                                            .value as string,
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    fontSize:
                                                                                        '0.75rem',
                                                                                }}
                                                                            >
                                                                                {TIME_OPTIONS.map(
                                                                                    (
                                                                                        time,
                                                                                    ) => (
                                                                                        <MenuItem
                                                                                            key={
                                                                                                time
                                                                                            }
                                                                                            value={
                                                                                                time
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                time
                                                                                            }
                                                                                        </MenuItem>
                                                                                    ),
                                                                                )}
                                                                            </Select>
                                                                        </FormControl>
                                                                        <Typography variant="caption">
                                                                            -
                                                                        </Typography>
                                                                        <FormControl
                                                                            size="small"
                                                                            variant="outlined"
                                                                            className={
                                                                                classes.timeInput
                                                                            }
                                                                        >
                                                                            <Select
                                                                                value={
                                                                                    daySchedule.endTime
                                                                                }
                                                                                onChange={(
                                                                                    e: React.ChangeEvent<{
                                                                                        value: unknown
                                                                                    }>,
                                                                                ) =>
                                                                                    handleScheduleChange(
                                                                                        step.id,
                                                                                        day,
                                                                                        'endTime',
                                                                                        e
                                                                                            .target
                                                                                            .value as string,
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    fontSize:
                                                                                        '0.75rem',
                                                                                }}
                                                                            >
                                                                                {TIME_OPTIONS.map(
                                                                                    (
                                                                                        time,
                                                                                    ) => (
                                                                                        <MenuItem
                                                                                            key={
                                                                                                time
                                                                                            }
                                                                                            value={
                                                                                                time
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                time
                                                                                            }
                                                                                        </MenuItem>
                                                                                    ),
                                                                                )}
                                                                            </Select>
                                                                        </FormControl>
                                                                    </>
                                                                ) : (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                        style={{
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        12:00 AM
                                                                        - 12:00
                                                                        AM
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )
                                                    })}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Collapsed Preview */}
                                    {!isExpanded && (
                                        <CardContent>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Box>
                                                    {step.subject && (
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                fontWeight: 500,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            {step.subject}
                                                        </Typography>
                                                    )}
                                                    {step.message && (
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {step.message.substring(
                                                                0,
                                                                100,
                                                            )}
                                                            {step.message
                                                                .length > 100
                                                                ? '...'
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    )}
                                </Card>
                            </Box>
                        )
                    })}

                    {/* Add Step Button - Only at the bottom */}
                    <Box
                        style={{
                            display: 'flex',
                            gap: 8,
                            marginTop: 16,
                            marginBottom: 8,
                        }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                setAddStepMenuAnchor({
                                    element: e.currentTarget,
                                    index: -1,
                                })
                            }}
                            className={classes.addDelayButton}
                        >
                            Add step
                        </Button>
                    </Box>

                    {/* Add Step Menu (single menu for all steps) */}
                    {addStepMenuAnchor && (
                        <Menu
                            anchorEl={addStepMenuAnchor.element}
                            open={Boolean(addStepMenuAnchor)}
                            onClose={() => setAddStepMenuAnchor(null)}
                        >
                            {channels.email && (
                                <MenuItem
                                    onClick={() => {
                                        const targetIndex =
                                            addStepMenuAnchor.index
                                        setAddStepMenuAnchor(null)
                                        handleAddStepAfter(targetIndex, 'email')
                                    }}
                                >
                                    <MailIcon
                                        style={{
                                            marginRight: 8,
                                            fontSize: 18,
                                        }}
                                    />
                                    Add Email Step
                                </MenuItem>
                            )}
                            {channels.sms && (
                                <MenuItem
                                    onClick={() => {
                                        const targetIndex =
                                            addStepMenuAnchor.index
                                        setAddStepMenuAnchor(null)
                                        handleAddStepAfter(targetIndex, 'sms')
                                    }}
                                >
                                    <MessageSquareIcon
                                        style={{
                                            marginRight: 8,
                                            fontSize: 18,
                                        }}
                                    />
                                    Add SMS Step
                                </MenuItem>
                            )}
                        </Menu>
                    )}
                </Box>
            )}

            {/* Navigation */}
            <Box className={classes.navigationContainer}>
                <Button
                    variant="outlined"
                    onClick={onBack}
                    startIcon={<ArrowLeft />}
                >
                    {backLabel}
                </Button>
                <Button
                    onClick={onNext}
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    className={classes.gradientButton}
                >
                    {nextLabel}
                </Button>
            </Box>

            {/* Create Guest Card Modal */}
            <CreateGuestCardModal
                open={createGuestCardModalOpen}
                onClose={() => {
                    setCreateGuestCardModalOpen(false)
                    setCurrentStepForGuestCard(null)
                }}
                onGuestCardCreated={(guestCardId) => {
                    if (currentStepForGuestCard) {
                        const updatedSequence = sequence.map((s) =>
                            s.id === currentStepForGuestCard
                                ? { ...s, guest_card: guestCardId }
                                : s,
                        )
                        setSequence(updatedSequence)
                    }
                    setCreateGuestCardModalOpen(false)
                    setCurrentStepForGuestCard(null)
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '8px',
                        minWidth: '400px',
                    },
                }}
            >
                <DialogContent style={{ padding: '24px', textAlign: 'center' }}>
                    <Box className={classes.deleteDialogIcon}>
                        <Delete style={{ fontSize: 32 }} />
                    </Box>
                    <DialogTitle
                        id="delete-dialog-title"
                        className={classes.deleteDialogTitle}
                        style={{ padding: 0 }}
                    >
                        Delete Step?
                    </DialogTitle>
                    <DialogContentText
                        id="delete-dialog-description"
                        className={classes.deleteDialogContent}
                        style={{ marginTop: '8px' }}
                    >
                        Are you sure you want to delete this step? This action
                        cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={classes.deleteDialogActions}>
                    <Button
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        style={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            textTransform: 'none',
                            padding: '8px 24px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        className={classes.deleteButton}
                        autoFocus
                        style={{
                            textTransform: 'none',
                            padding: '8px 24px',
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Generation Dialog */}
            <Dialog
                open={aiDialogOpen}
                onClose={() => setAiDialogOpen(false)}
                className={classes.aiDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '16px',
                        minWidth: '650px',
                        maxWidth: '750px',
                    },
                }}
            >
                <DialogTitle style={{ padding: '24px 24px 16px 24px' }}>
                    <Box className={classes.aiDialogTitle}>
                        <Box className={classes.aiDialogIcon}>
                            <AIIcon style={{ fontSize: 24 }} />
                        </Box>
                        Generate Emails with AI
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.aiDialogContent}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 24 }}
                    >
                        Let AI create professional email sequences for your
                        campaign. Provide details about your property to
                        generate personalized, compelling messages.
                        {campaignName && (
                            <span style={{ display: 'block', marginTop: 8 }}>
                                <strong>Campaign:</strong> {campaignName}
                            </span>
                        )}
                    </Typography>

                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                        }}
                    >
                        <TextField
                            fullWidth
                            label="Number of Steps"
                            type="number"
                            value={aiNumSteps}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                                setAiNumSteps(
                                    Math.max(
                                        1,
                                        Math.min(
                                            10,
                                            parseInt(e.target.value) || 1,
                                        ),
                                    ),
                                )
                            }
                            inputProps={{ min: 1, max: 10 }}
                            variant="outlined"
                            className={classes.formField}
                            helperText="How many email steps to generate (1-10)"
                        />

                        <FormControl
                            fullWidth
                            variant="outlined"
                            className={classes.formField}
                        >
                            <InputLabel>Tone</InputLabel>
                            <Select
                                value={aiTone}
                                onChange={(
                                    e: React.ChangeEvent<{ value: unknown }>,
                                ) => setAiTone(e.target.value as string)}
                                label="Tone"
                            >
                                <MenuItem value="professional">
                                    Professional
                                </MenuItem>
                                <MenuItem value="friendly">Friendly</MenuItem>
                                <MenuItem value="casual">Casual</MenuItem>
                                <MenuItem value="urgent">Urgent</MenuItem>
                                <MenuItem value="formal">Formal</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Property Details"
                            multiline
                            rows={5}
                            placeholder="e.g., Pet-friendly community with pool and fitness center. Located in downtown near public transit. Studios from $1,500/mo, 1BR from $1,800/mo. Move-in specials available..."
                            value={aiAdditionalContext}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => setAiAdditionalContext(e.target.value)}
                            variant="outlined"
                            className={classes.formField}
                            required
                            helperText="Include amenities, location highlights, pricing, move-in specials, and anything else prospects should know"
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    style={{ padding: '16px 24px 24px 24px', gap: 8 }}
                >
                    <Button
                        onClick={() => setAiDialogOpen(false)}
                        variant="outlined"
                        style={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            textTransform: 'none',
                            padding: '8px 24px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateAI}
                        variant="contained"
                        className={classes.aiGenerateButton}
                        disabled={
                            generateAIEmails.isLoading ||
                            !aiAdditionalContext.trim()
                        }
                        style={{
                            textTransform: 'none',
                            padding: '8px 24px',
                        }}
                    >
                        {generateAIEmails.isLoading ? (
                            <>
                                <CircularProgress
                                    size={18}
                                    style={{ marginRight: 8, color: 'inherit' }}
                                />
                                Generating...
                            </>
                        ) : (
                            'Generate Emails'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
